import React, { useEffect, useMemo, useState } from 'react';
import type { FC, FormEventHandler } from 'react';

import type { AxiosResponse } from 'axios';
import axios from 'axios';

import { Button, Checkbox, Col, ControlLabel, FormGroup, Modal, ModalSize, Row } from '@shared/components/bootstrap';
import { InputFormControl } from '@shared/components/fields/input_form_control';
import { TextAreaFormControl } from '@shared/components/fields/text_area_form_control';
import { Percent } from '@shared/components/helpers/percent';

import { useAxiosRequest } from '@admin/hooks';

import type { DispatcherDispatchRecord } from './dispatcher_types';

type Assignment = {
  id?: string | number | null;
  user?: { roles?: string[] };
};

type VehiclePreference = NonNullable<DispatcherDispatchRecord['vehicle_preferences']>[number];

const driverCount = (assignments: Assignment[] = []) =>
  assignments.filter((assignment) => assignment.user?.roles?.includes('driver')).length;

type VehicleType = {
  id: number;
  name: string;
  cuft: string;
  field_dispatchable: boolean;
  num_seats: number;
};

const useVechileTypeRequest = () => {
  const [request] = useState<Promise<AxiosResponse<VehicleType[]>>>(() => axios.get('/vehicle_types.json'));
  return useAxiosRequest(request);
};

export const DispatcherVehicleRequestDialog: FC<{
  dispatch: DispatcherDispatchRecord;
  onClose(): void;
  onSave(): void;
}> = ({ dispatch, onClose, onSave }) => {
  const { response } = useVechileTypeRequest();
  const assignments = dispatch.assignments ?? [];
  const orders = dispatch.orders ?? [];
  const drivers = driverCount(assignments);

  const [preferences, setPreferences] = useState<VehiclePreference[]>(() => dispatch.vehicle_preferences ?? []);
  const [recipeMinQuantity, setRecipeMinQuantity] = useState<string>(() =>
    String(dispatch.vehicle_recipe?.min_quantity ?? 1),
  );
  const [recipeMaxQuantity, setRecipeMaxQuantity] = useState<string>(() =>
    String(dispatch.vehicle_recipe?.max_quantity ?? (drivers || '')),
  );
  const [recipeMinimumCuft, setRecipeMinimumCuft] = useState<string>(() =>
    String(dispatch.vehicle_recipe?.minimum_cuft ?? ''),
  );
  const [recipeAllowedVehicleTypeIDs, setRecipeAllowedVehicleTypeIDs] = useState<Set<number>>(
    () => new Set(dispatch.vehicle_recipe?.allowed_vehicle_type_ids?.map((id) => Number(id)) ?? []),
  );

  const [notes, setNotes] = useState<string>(() => dispatch.vehicle_recipe?.notes ?? '');

  const types = useMemo(() => response?.data?.filter((type) => type.field_dispatchable), [response?.data]);

  useEffect(() => {
    if (!types?.length || dispatch.vehicle_recipe?.allowed_vehicle_type_ids != null) {
      return;
    }

    setRecipeAllowedVehicleTypeIDs(new Set(types.map((type) => type.id)));
  }, [types, dispatch.vehicle_recipe?.allowed_vehicle_type_ids]);

  const onSubmit: FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();

    dispatch.vehicle_preferences = preferences
      .map((preference) => {
        const quantity = preference.quantity ? Number(preference.quantity) : 0;
        const _destroy = !quantity;
        return { id: preference.id, vehicle_type_id: preference.vehicle_type_id, quantity, _destroy };
      })
      .filter((preference) => preference.id || !preference._destroy);

    const hasRecipeInput =
      recipeMinQuantity || recipeMaxQuantity || recipeMinimumCuft || recipeAllowedVehicleTypeIDs.size > 0 || notes;

    if (hasRecipeInput || dispatch.vehicle_recipe) {
      dispatch.vehicle_recipe = {
        id: dispatch.vehicle_recipe?.id,
        min_quantity: recipeMinQuantity ? Number(recipeMinQuantity) : 0,
        max_quantity: recipeMaxQuantity ? Number(recipeMaxQuantity) : 0,
        minimum_cuft: recipeMinimumCuft ? Number(recipeMinimumCuft) : 0,
        allowed_vehicle_type_ids: Array.from(recipeAllowedVehicleTypeIDs),
        notes: notes ? notes : undefined,
      };
    }

    onSave();
  };

  const preferencesByVehicleTypeID = preferences.reduce<Record<string, VehiclePreference>>(
    (accumulator, preference) => {
      accumulator[preference.vehicle_type_id] = preference;
      return accumulator;
    },
    {} as Record<string, VehiclePreference>,
  );

  const typesByID = types?.reduce<Record<string, VehicleType>>(
    (accumulator, type) => {
      accumulator[type.id] = type;
      return accumulator;
    },
    {} as Record<string, VehicleType>,
  );

  const seats =
    preferences?.reduce((accumulator, preference) => {
      if (!typesByID) return accumulator;
      const type = typesByID[preference.vehicle_type_id];
      if (!type) return accumulator;
      const quantity = preference.quantity ? Number(preference.quantity) : 0;
      return accumulator + Number(type.num_seats) * quantity;
    }, 0) ?? 0;

  const availableCUFT =
    preferences?.reduce((accumulator, preference) => {
      if (!typesByID) return accumulator;
      const type = typesByID[preference.vehicle_type_id];
      if (!type) return accumulator;
      const quantity = preference.quantity ? Number(preference.quantity) : 0;
      return accumulator + Number(type.cuft) * quantity;
    }, 0) ?? 0;

  const returnCuft = orders.reduce((accumulator, order) => {
    if (order.items_cuft == null) return accumulator;
    return accumulator + Number(order.items_cuft);
  }, 0);

  const returnCuftPercentage = availableCUFT > 0 && returnCuft > 0 ? returnCuft / availableCUFT : 0;

  return (
    <Modal size={ModalSize.Large} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Vehicle Preferences / Requirements</Modal.Title>
            <Modal.Close close={onClose} />
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <h3>Vehicle Preferences</h3>

                <hr />

                {types?.map((type, index) => (
                  <FormGroup key={type.id}>
                    <ControlLabel htmlFor={`preferences[${type.id}][quantity]`}>{type.name}</ControlLabel>
                    <InputFormControl
                      id={`preferences[${type.id}][quantity]`}
                      name={`preferences[${type.id}][quantity]`}
                      type="number"
                      min={0}
                      maxLength={2}
                      value={(() => {
                        const preference = preferencesByVehicleTypeID[type.id];
                        return !preference || preference._destroy ? '' : String(preference?.quantity);
                      })()}
                      onChange={(event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        const quantity = event.target.value ? Number(event.target.value) : 0;
                        const _destroy = !event.target.value;

                        setPreferences((current) => {
                          const exists = current.some(
                            ({ vehicle_type_id }) => String(vehicle_type_id) === String(type.id),
                          );

                          if (exists)
                            return current.map((preference) =>
                              String(preference.vehicle_type_id) === String(type.id)
                                ? { ...preference, quantity, _destroy }
                                : preference,
                            );
                          else return [...current, { vehicle_type_id: type.id, quantity, _destroy }];
                        });
                      }}
                    />
                  </FormGroup>
                ))}

                <div>
                  Return CUFT: {returnCuft.toLocaleString(undefined, { maximumFractionDigits: 2 })} (
                  <Percent value={returnCuftPercentage} /> of selected vehicles)
                </div>
                <div>Seats Needed: {assignments.length}</div>
                <div>Seats Available: {seats}</div>
              </Col>

              <Col md={8}>
                <h3>Vehicle Requirements</h3>

                <hr />

                <Row>
                  <Col md={6}>
                    <ControlLabel>Allowed Vehicle Types:</ControlLabel>
                    {types?.map((type) => (
                      <Checkbox key={type.id}>
                        <input
                          type="checkbox"
                          checked={recipeAllowedVehicleTypeIDs.has(type.id)}
                          onChange={(event) => {
                            setRecipeAllowedVehicleTypeIDs((current) => {
                              const changes = new Set(current);
                              if (event.target.checked) changes.add(type.id);
                              else changes.delete(type.id);
                              return changes;
                            });
                          }}
                        />
                        {type.name} ({type.cuft} cuft)
                      </Checkbox>
                    ))}
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <ControlLabel htmlFor="recipe_min_vehicles">Min Vehicles:</ControlLabel>
                      <InputFormControl
                        id="recipe_min_vehicles"
                        name="recipe_min_vehicles"
                        type="number"
                        value={recipeMinQuantity}
                        onChange={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setRecipeMinQuantity(event.target.value);
                        }}
                        min={0}
                        maxLength={2}
                      />
                    </FormGroup>
                    <FormGroup>
                      <ControlLabel htmlFor="recipe_max_vehicles">Max Vehicles:</ControlLabel>
                      <InputFormControl
                        id="recipe_max_vehicles"
                        name="recipe_max_vehicles"
                        type="number"
                        value={recipeMaxQuantity}
                        onChange={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setRecipeMaxQuantity(event.target.value);
                        }}
                        min={0}
                        maxLength={2}
                      />
                    </FormGroup>
                    <FormGroup>
                      <ControlLabel htmlFor="recipe_min_cuft">Minimum Total Truck Cuft:</ControlLabel>
                      <InputFormControl
                        id="recipe_min_cuft"
                        name="recipe_min_cuft"
                        type="number"
                        min={0}
                        value={recipeMinimumCuft}
                        onChange={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setRecipeMinimumCuft(event.target.value);
                        }}
                      />
                    </FormGroup>
                    <div>Drivers on Dispatch: {drivers}</div>
                  </Col>
                </Row>
                <FormGroup>
                  <ControlLabel htmlFor="vehicle_request_notes">Vehicle Request Notes:</ControlLabel>
                  <TextAreaFormControl
                    id="vehicle_request_notes"
                    value={notes}
                    onChange={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setNotes(event.target.value);
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" kind="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" kind="primary" disabled={!types}>
              Done
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </form>
    </Modal>
  );
};
